import { useDictionary, useTranslator } from "gemi/client";
import { useEffect, useRef, useState } from "react";
import { useV2AuthEntry } from "./AuthModalV2";
import { type AuthIntentPill, encodeImagesForCarry } from "./authIntent";
import { heroPromptDict } from "./HeroPrompt.i18n";

/*
 * Hero composer — intent capture, not anonymous generation. The visitor types a prompt, optionally
 * attaches product images, and picks a tool pill (Create image / Create video). "Run" stashes the
 * intent (prompt + pill + images) and opens the sign-up modal; the real work happens in the authed
 * chat composer, which reads the intent, pre-selects the tool (WorkspaceInputMode resolves it from
 * pill + attachment count: image→text-to-image/edit, video→text/image/reference-to-video) and
 * prefills the prompt + images. The pill only swaps the placeholder here — no config panel.
 * The store pill starts the account's store setup in the chat instead, so it can run with no text.
 */

const MAX_IMAGES = 10; // matches the chat composer's attachment cap

type PillType = Exclude<AuthIntentPill, null>;
type PillConfig = { type: PillType; icon: string };

const PILLS: PillConfig[] = [
  { type: "image", icon: "image-edit" },
  { type: "video", icon: "video-camera" },
  { type: "store", icon: "storefront" },
];

type Attachment = { name: string; url: string; file: File };

export function HeroPrompt() {
  const t = useTranslator("HomePageV2");
  const tHero = useDictionary(heroPromptDict);
  const enterApp = useV2AuthEntry();
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [activePill, setActivePill] = useState<PillType | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [carryWarning, setCarryWarning] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const count = attachments.length;
  const placeholder =
    activePill === "image"
      ? t("prompt_placeholder_image")
      : activePill === "video"
        ? t("prompt_placeholder_video")
        : activePill === "store"
          ? tHero("prompt_placeholder_store")
          : t("prompt_placeholder");
  const pillLabel = (type: PillType) =>
    type === "image" ? t("chip_image") : type === "video" ? t("chip_video") : tHero("chip_store");

  const onPickFiles = (list: FileList | null) => {
    if (!list) return;
    const incoming = Array.from(list).filter((f) => f.type.startsWith("image/"));
    setAttachments((prev) => {
      const room = MAX_IMAGES - prev.length;
      const next = incoming
        .slice(0, room)
        .map((file) => ({ name: file.name, url: URL.createObjectURL(file), file }));
      return [...prev, ...next];
    });
    setCarryWarning(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAttachment = (index: number) => {
    setAttachments((prev) => {
      const target = prev[index];
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((_, i) => i !== index);
    });
    setCarryWarning(false);
  };

  const submit = async () => {
    if (submitting) return;
    const text = message.trim();
    if (text.length === 0 && count === 0 && activePill !== "store") return;
    setSubmitting(true);
    const { images, complete } = await encodeImagesForCarry(
      attachments.map((a) => a.file),
    );
    if (!mounted.current) return;
    // All-or-nothing: if the photos can't all ride along, don't silently hand off a different tool.
    // Surface it once and let the visitor confirm (re-submit) before proceeding without them.
    if (!complete && !carryWarning) {
      setCarryWarning(true);
      setSubmitting(false);
      return;
    }
    enterApp({
      target: "chat",
      composer: { prompt: text, pill: activePill, images },
    });
    setSubmitting(false);
  };

  return (
    <div className="mt-12 w-full lg:mt-16 lg:max-w-[768px]">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-col gap-6 rounded-[10px] border border-[#8eebd2] bg-white px-[11px] pb-4 pt-[21px] lg:rounded-[20px]"
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          hidden
          onChange={(e) => onPickFiles(e.target.files)}
        />

        <div className="flex flex-col gap-3">
          {count > 0 && (
            <div className="flex flex-wrap gap-2">
              {attachments.map((item, index) => (
                <div key={`${item.name}-${index}`} className="relative w-fit">
                  <img src={item.url} alt={item.name} className="size-16 rounded-[8px] object-cover" />
                  <button
                    type="button"
                    onClick={() => removeAttachment(index)}
                    aria-label={t("attachment_remove")}
                    className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full bg-black/60 text-[12px] leading-none text-white"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
          <textarea
            rows={2}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={placeholder}
            autoComplete="off"
            data-form-type="other"
            className="m-0 w-full resize-none border-none bg-transparent text-left text-[14px] text-[#343339] outline-none placeholder:text-[#343339] placeholder:opacity-80 lg:text-[16px]"
          />
        </div>

        {carryWarning && (
          <p role="status" className="m-0 text-[12px] leading-[1.35] text-[#c2410c]">
            {t("carry_warning")}
          </p>
        )}

        <div className="flex items-center justify-between gap-2">
          <div className="no-scrollbar flex min-w-0 flex-1 items-center gap-1 overflow-x-auto lg:gap-1.5">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              aria-label={t("chip_attach")}
              className="flex h-[21px] flex-none items-center justify-center gap-1 rounded-[6px] border-[0.7px] border-dashed border-[rgba(52,51,57,0.6)] px-[7px] lg:h-[30px] lg:rounded-[8px] lg:border lg:px-[10px]"
            >
              <img src="/v2/icons/upload-simple.svg" alt="" aria-hidden className="block size-[14px] lg:size-5" />
              <span className="hidden text-[10px] font-medium tracking-[0.1px] text-[#343339] opacity-80 lg:block">
                {t("chip_attach")}
              </span>
            </button>
            {PILLS.map((pill) => {
              const active = activePill === pill.type;
              return (
                <button
                  key={pill.type}
                  type="button"
                  aria-pressed={active}
                  // The label below is hidden under lg, so the button needs its own accessible name.
                  aria-label={pillLabel(pill.type)}
                  onClick={() => setActivePill((prev) => (prev === pill.type ? null : pill.type))}
                  className={`flex h-[21px] flex-none items-center justify-center gap-1 rounded-[6px] px-[7px] transition-colors lg:h-[30px] lg:rounded-[8px] lg:px-[10px] ${
                    active ? "bg-[rgba(52,51,57,0.22)]" : "bg-[rgba(183,183,183,0.3)]"
                  }`}
                >
                  <img src={`/v2/icons/${pill.icon}.svg`} alt="" aria-hidden className="block size-[14px] lg:size-5" />
                  <span className="hidden text-[10px] font-medium tracking-[0.1px] text-[#343339] opacity-80 lg:block">
                    {pillLabel(pill.type)}
                  </span>
                </button>
              );
            })}
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="flex flex-none items-center justify-center rounded-full bg-[#343339] px-[13px] py-[7px] text-[10px] font-medium text-white no-underline disabled:opacity-60 lg:px-5 lg:py-2.5 lg:text-[14px]"
          >
            {t("run")} <span aria-hidden>&nbsp;→</span>
          </button>
        </div>
      </form>
    </div>
  );
}
